/**
 * PublicBlogPage.jsx
 * 
 * Public-facing blog page for SEO.
 * Sleek, modern flat design with custom shadows.
 * Shows full blog content with related links, service CTAs, and search phrases.
 * Includes comprehensive SEO meta tags for search engine visibility.
 */

import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import ReactMarkdown from 'react-markdown';
import { 
  Calendar, MapPin, Building2, Shield, Globe, 
  ExternalLink, Clock, Loader2, Star, ChevronLeft, Hash
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { ExperienceCard } from '../components/marketplace';
import { BACKEND_URL } from '../config/api';

const fetcher = url => fetch(url).then(r => r.json());

// Helper to set meta tags dynamically
const setMetaTag = (name, content, isProperty = false) => {
  const attr = isProperty ? 'property' : 'name';
  let meta = document.querySelector(`meta[${attr}="${name}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute(attr, name);
    document.head.appendChild(meta);
  }
  meta.content = content;
};

const PublicBlogPage = () => {
  const { slug: orgSlug } = useParams();
  const navigate = useNavigate();
  
  const { data: blog, error, isLoading } = useSWR(
    orgSlug ? `${BACKEND_URL}/api/v2/blogs/public/${orgSlug}/blog` : null,
    fetcher,
    { revalidateOnFocus: false }
  );
  
  // Fetch org's services for CTA
  const { data: servicesData } = useSWR(
    blog?.organization?.id ? `${BACKEND_URL}/api/v2/public/services?org_id=${blog.organization.id}` : null,
    fetcher,
    { revalidateOnFocus: false }
  );
  
  // Fetch operator details for reviews/rating
  const { data: operatorData } = useSWR(
    blog?.organization?.id ? `${BACKEND_URL}/api/marketplace/operators/${blog.organization.id}` : null,
    fetcher,
    { revalidateOnFocus: false }
  );
  
  const services = servicesData?.services || [];
  const operator = operatorData || {};
  
  // Build SEO data (safe to compute even when blog is null)
  const org = blog?.organization;
  const searchPhrases = blog?.seo_meta?.search_phrases || [];
  const competitorKeywords = blog?.seo_meta?.competitor_keywords || [];
  const seoTitle = blog ? `${blog.title} | ${org?.name || 'BookingFlows'}` : 'Blog | BookingFlows';
  const seoDescription = blog?.excerpt || blog?.seo_meta?.description || blog?.content?.substring(0, 160) || '';
  const seoKeywords = [
    ...(blog?.seo_meta?.keywords || []),
    ...searchPhrases,
    ...competitorKeywords
  ].join(', ');
  const canonicalUrl = `${window.location.origin}/blog/${orgSlug}`;
  const ogImage = org?.logo_url || `${window.location.origin}/logo-icon.png`;
  const publishDate = blog?.published_at || blog?.created_at || '';
  
  // Set meta tags dynamically for SEO crawlers
  useEffect(() => {
    if (!blog) return;
    
    // Update document title
    document.title = seoTitle;
    
    // Set meta tags
    setMetaTag('description', seoDescription);
    setMetaTag('keywords', seoKeywords);
    setMetaTag('author', org?.name || 'BookingFlows');
    
    // Open Graph
    setMetaTag('og:type', 'article', true);
    setMetaTag('og:url', canonicalUrl, true);
    setMetaTag('og:title', seoTitle, true);
    setMetaTag('og:description', seoDescription, true);
    setMetaTag('og:image', ogImage, true);
    setMetaTag('og:site_name', 'BookingFlows', true);
    setMetaTag('article:published_time', publishDate, true);
    setMetaTag('article:author', org?.name, true);
    
    // Twitter
    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('twitter:url', canonicalUrl);
    setMetaTag('twitter:title', seoTitle);
    setMetaTag('twitter:description', seoDescription);
    setMetaTag('twitter:image', ogImage);
    
    // Canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;
    
    // JSON-LD Schema
    let jsonLd = document.querySelector('script[type="application/ld+json"]');
    if (!jsonLd) {
      jsonLd = document.createElement('script');
      jsonLd.type = 'application/ld+json';
      document.head.appendChild(jsonLd);
    }
    jsonLd.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": blog.title,
      "description": seoDescription,
      "image": ogImage,
      "author": {
        "@type": "Organization",
        "name": org?.name || 'BookingFlows',
        "url": `${window.location.origin}/host/${org?.id}`
      },
      "publisher": {
        "@type": "Organization",
        "name": "BookingFlows",
        "logo": {
          "@type": "ImageObject",
          "url": `${window.location.origin}/logo-icon.png`
        }
      },
      "datePublished": publishDate,
      "dateModified": blog.updated_at || publishDate,
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": canonicalUrl
      },
      "keywords": seoKeywords
    });
  }, [blog, seoTitle, seoDescription, seoKeywords, canonicalUrl, ogImage, publishDate, org]);
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }
  
  if (error || !blog) {
    return (
      <div className="min-h-screen bg-white">
        {/* Standard Nav */}
        <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md pt-[env(safe-area-inset-top)]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="flex items-center h-14">
              <Link to="/" className="flex items-center gap-2">
                <img 
                  src="/logo-icon.png" 
                  alt="BookingFlows" 
                  className="h-8 w-8 object-contain rounded-lg"
                />
                <span className="text-lg font-semibold text-gray-900">BookingFlows</span>
              </Link>
            </div>
          </div>
        </nav>
        <div className="flex flex-col items-center justify-center py-20 px-4">
          <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-6">
            <Globe className="w-8 h-8 text-gray-400" />
          </div>
          <h1 className="text-lg font-semibold text-gray-900 mb-2">Blog Not Found</h1>
          <p className="text-gray-500 mb-6 text-center text-sm">This blog post may not exist or has not been published yet.</p>
          <button 
            onClick={() => navigate('/')} 
            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to marketplace
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Navigation - Clean, minimal */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md pt-[env(safe-area-inset-top)]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <button 
                onClick={() => navigate('/blog')}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                data-testid="back-button"
              >
                <ChevronLeft className="w-5 h-5 text-gray-500" />
              </button>
              <Link to="/" className="flex items-center gap-2">
                <img 
                  src="/logo-icon.png" 
                  alt="BookingFlows" 
                  className="h-7 w-7 object-contain rounded-lg"
                />
                <span className="text-base font-semibold text-gray-900 hidden sm:inline">BookingFlows</span>
              </Link>
            </div>
            <Link to={`/host/${org?.id}`}>
              <Button variant="outline" size="sm" className="rounded-full text-xs h-8 px-3 border-gray-200">
                View {org?.name}
              </Button>
            </Link>
          </div>
        </div>
      </nav>
      
      {/* Content */}
      <main className="flex-1 max-w-4xl mx-auto px-4 py-8 w-full">
        {/* Blog Card - Flat design with custom shadow */}
        <article 
          className="bg-white rounded-3xl overflow-hidden mb-8"
          style={{ boxShadow: '0 4px 24px -4px rgba(0, 0, 0, 0.08), 0 2px 8px -2px rgba(0, 0, 0, 0.04)' }}
        >
          {/* Hero Header */}
          <div className="bg-[#04091B] p-8 sm:p-10">
            <div className="flex flex-col sm:flex-row sm:items-start gap-6">
              {org?.logo_url && (
                <Link to={`/host/${org?.id}`} className="flex-shrink-0">
                  <div 
                    className="w-16 h-16 sm:w-20 sm:h-20 bg-white rounded-xl p-2 flex items-center justify-center"
                    style={{ boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)' }}
                  >
                    <img 
                      src={org.logo_url} 
                      alt={org.name}
                      className="w-full h-full object-contain"
                    />
                  </div>
                </Link>
              )}
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl sm:text-3xl font-bold text-white mb-3 leading-tight">{blog.title}</h1>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-slate-300 text-sm">
                  <span className="flex items-center gap-1.5">
                    <Calendar className="w-4 h-4" />
                    {new Date(blog.published_at || blog.created_at).toLocaleDateString('en-US', { 
                      month: 'long', day: 'numeric', year: 'numeric' 
                    })}
                  </span>
                  {org?.city && (
                    <span className="flex items-center gap-1.5">
                      <MapPin className="w-4 h-4" />
                      {org.city}{org.state ? `, ${org.state}` : ''}
                    </span>
                  )}
                </div>
                {/* Company byline with rating */}
                <Link 
                  to={`/host/${org?.id}`}
                  className="inline-flex items-center gap-2 mt-4 text-slate-400 hover:text-white transition-colors text-sm"
                >
                  by <span className="font-medium text-white">{org?.name}</span>
                  {operator.averageRating > 0 && (
                    <span className="flex items-center gap-1 bg-white/10 px-2 py-0.5 rounded-full text-xs">
                      <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                      {operator.averageRating}
                    </span>
                  )}
                </Link>
              </div>
            </div>
          </div>
          
          {/* SEO Keywords */}
          {blog.seo_meta?.keywords?.length > 0 && (
            <div className="px-8 sm:px-10 pt-6 flex flex-wrap gap-2">
              {blog.seo_meta.keywords.map((keyword, i) => (
                <span key={i} className="px-3 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full">
                  {keyword}
                </span>
              ))}
            </div>
          )}
          
          {/* Blog Content */}
          <div className="px-8 sm:px-10 py-8">
            <div className="max-w-none text-gray-700 leading-relaxed">
              <ReactMarkdown
                components={{
                  // H2 headings - clean, professional
                  h2: ({children}) => (
                    <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-4">
                      {children}
                    </h2>
                  ),
                  // H3 headings - BookingFlows blue accent
                  h3: ({children}) => (
                    <h3 className="text-lg font-medium text-blue-600 mt-6 mb-3">
                      {children}
                    </h3>
                  ),
                  // Paragraphs - comfortable reading
                  p: ({children}) => (
                    <p className="text-gray-600 leading-7 mb-4 text-[15px]">
                      {children}
                    </p>
                  ),
                  // Bold text - subtle emphasis, not heavy
                  strong: ({children}) => (
                    <span className="text-gray-800 font-medium">
                      {children}
                    </span>
                  ),
                  // Emphasis/italics
                  em: ({children}) => (
                    <em className="text-gray-700 italic">
                      {children}
                    </em>
                  ),
                  // Lists
                  ul: ({children}) => (
                    <ul className="my-4 ml-4 space-y-2">
                      {children}
                    </ul>
                  ),
                  li: ({children}) => (
                    <li className="text-gray-600 text-[15px] leading-7 flex gap-2">
                      <span className="text-blue-600 leading-7">•</span>
                      <span>{children}</span>
                    </li>
                  ),
                  // Links - BookingFlows blue
                  a: ({href, children}) => (
                    <a 
                      href={href} 
                      className="text-blue-600 hover:text-blue-700 underline underline-offset-2"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {children}
                    </a>
                  ),
                }}
              >
                {blog.content}
              </ReactMarkdown>
            </div>
          </div>
          
          {/* Visit Website CTA - Use blog's website_url first, then operator's */}
          {(blog.website_url || operator.website) && (
            <div className="px-8 sm:px-10 pb-8">
              <a
                href={(blog.website_url || operator.website).startsWith('http') 
                  ? (blog.website_url || operator.website) 
                  : `https://${blog.website_url || operator.website}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-slate-900 text-white rounded-full text-sm font-medium hover:bg-slate-800 transition-colors"
              >
                <Globe className="w-4 h-4" />
                Visit {org?.name}&apos;s Website
                <ExternalLink className="w-3.5 h-3.5 opacity-60" />
              </a>
            </div>
          )}
        </article>
        
        {/* Related Links */}
        {blog.related_links?.length > 0 && (
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Related Resources</h2>
            <div 
              className="bg-white rounded-3xl p-6 grid grid-cols-1 sm:grid-cols-2 gap-3"
              style={{ boxShadow: '0 4px 24px -4px rgba(0, 0, 0, 0.08), 0 2px 8px -2px rgba(0, 0, 0, 0.04)' }}
            >
              {blog.related_links.map((link, index) => (
                <a
                  key={index}
                  href={link.url}
                  target={link.url.startsWith('http') ? '_blank' : '_self'}
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors group"
                >
                  <div className={`p-2.5 rounded-xl flex-shrink-0 ${
                    link.type === 'business' ? 'bg-blue-50' :
                    link.type === 'regulation' ? 'bg-emerald-50' :
                    'bg-purple-50'
                  }`}>
                    {link.type === 'business' ? <Building2 className="w-4 h-4 text-blue-600" /> :
                     link.type === 'regulation' ? <Shield className="w-4 h-4 text-emerald-600" /> :
                     <Globe className="w-4 h-4 text-purple-600" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 text-sm group-hover:text-blue-600 transition-colors truncate">
                      {link.name}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{link.description || link.type}</p>
                  </div>
                  <ExternalLink className="w-3.5 h-3.5 text-gray-300 group-hover:text-blue-500 flex-shrink-0" />
                </a>
              ))}
            </div>
          </section>
        )}
        
        {/* Services CTA */}
        {services.length > 0 && (
          <section className="mb-8">
            <div className="text-center mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-1">Book Your Experience</h2>
              <p className="text-sm text-gray-500">Ready to get started with {org?.name}?</p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {services.slice(0, 3).map((service) => (
                <ExperienceCard 
                  key={service.id}
                  service={{
                    ...service,
                    city: service.city || org?.city,
                    operator: {
                      name: org?.name,
                      rating: operator.averageRating,
                      reviewCount: operator.reviewCount
                    }
                  }}
                />
              ))}
            </div>
            
            {services.length > 3 && (
              <div className="text-center mt-6">
                <Link to={`/host/${org?.id}`}>
                  <Button variant="outline" size="sm" className="rounded-full text-sm">
                    View All {services.length} Experiences
                  </Button>
                </Link>
              </div>
            )}
          </section>
        )}
        
        {/* Search Phrases - SEO Hashtags */}
        {(searchPhrases.length > 0 || competitorKeywords.length > 0 || blog.seo_meta?.keywords?.length > 0) && (
          <section 
            className="bg-white rounded-3xl p-6 mb-8"
            style={{ boxShadow: '0 4px 24px -4px rgba(0, 0, 0, 0.08), 0 2px 8px -2px rgba(0, 0, 0, 0.04)' }}
          >
            <div className="flex items-center gap-2 mb-4">
              <Hash className="w-4 h-4 text-gray-400" />
              <h3 className="text-sm font-medium text-gray-500">Related Searches</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {/* Combine all search terms */}
              {[...new Set([
                ...(blog.seo_meta?.keywords || []),
                ...searchPhrases,
                ...competitorKeywords
              ])].map((phrase, i) => (
                <Link
                  key={i}
                  to={`/search?q=${encodeURIComponent(phrase)}`}
                  className="px-3 py-1.5 bg-gray-100 text-gray-600 text-xs rounded-full hover:bg-gray-200 hover:text-gray-900 transition-colors"
                >
                  {phrase}
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
      
      {/* Footer - Minimal */}
      <footer className="py-8 px-4 bg-white border-t border-gray-100 mt-auto">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img 
                src="/logo-icon.png" 
                alt="BookingFlows" 
                className="h-6 w-6 object-contain rounded-lg"
              />
              <span className="text-sm font-semibold text-gray-900">BookingFlows</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-gray-500">
              <Link to="/" className="hover:text-gray-900 transition-colors">Home</Link>
              <Link to="/search" className="hover:text-gray-900 transition-colors">Experiences</Link>
              <Link to={`/host/${org?.id}`} className="hover:text-gray-900 transition-colors">{org?.name}</Link>
              <Link to="/blog" className="hover:text-gray-900 transition-colors">Blog</Link>
            </div>
            <p className="text-xs text-gray-400">
              © {new Date().getFullYear()} BookingFlows
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicBlogPage;
